/**
 * @file Layout.tsx
 * @description This file defines the main Layout component for the application.
 * It includes the header, navigation links, and a placeholder for page content.
 * It also handles responsive navigation and displays user authentication status.
 */

import React, { useState, useEffect, useRef, RefObject } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Layout.module.css";

import { useAuth } from "../../auth/authProvider";
import { IconButton, Icon } from "@fluentui/react";

interface UserInfo {
    username: string;
    isDev: boolean;
    can_manage_documents: boolean;
    categories: string[]; // Catégories Azure AI Search
    categories_google: string[]; // Catégories Google RAG (FileSearch)
}

/**
 * @component Layout
 * @description The main layout component that wraps all pages.
 * It provides a consistent header with navigation, a title, and authentication controls.
 * The `<Outlet />` component renders the content of the current route.
 */
const Layout = () => {
    // Hook for internationalization.
    const { t } = useTranslation();
    const location = useLocation();
    // Hook to get authentication details and functions.
    const { isAuthenticated, isLocalAuth, isSocialAuth, username, logout, getAuthToken, isLoading } = useAuth();
    // State to manage the visibility of the mobile navigation menu.
    const [menuOpen, setMenuOpen] = useState(false);
    // Ref to the menu container DOM element, used for detecting clicks outside the menu.
    const menuRef: RefObject<HTMLDivElement> = useRef(null);
    const userInfoRetryRef = useRef(0);
    // State for user info with permissions
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loadingUserInfo, setLoadingUserInfo] = useState(true);

    /**
     * @function toggleMenu
     * @description Toggles the `menuOpen` state to show or hide the navigation menu.
     */
    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    /**
     * @function fetchUserInfo
     * @description Fetches user information including permissions from the backend.
     */
    const fetchUserInfo = async () => {
        try {
            const token = await getAuthToken();
            if (!token) {
                if (userInfoRetryRef.current < 3) {
                    userInfoRetryRef.current += 1;
                    setTimeout(() => {
                        fetchUserInfo();
                    }, 500);
                    return;
                }
                setLoadingUserInfo(false);
                return;
            }
            const headers: Record<string, string> = {
                "Content-Type": "application/json"
            };

            headers["Authorization"] = `Bearer ${token}`;

            const response = await fetch("/user_categories_postgresql", {
                method: "GET",
                headers: headers
            });

            if (response.ok) {
                const data = await response.json();
                setUserInfo(data);
            } else {
                console.error("Failed to fetch user info:", response.status);
                if (response.status === 401) {
                    // Token expired or invalid — logout to force re-login
                    logout();
                }
            }
        } catch (err) {
            console.error("Error fetching user info:", err);
        } finally {
            setLoadingUserInfo(false);
        }
    };

    /**
     * @function isActive
     * @description Checks if a given path is the current active route.
     */
    const isActive = (path: string) => {
        return location.pathname === path;
    };

    /**
     * @function handleClickOutside
     * @description Closes the menu if a click is detected outside of the menu's container.
     * @param {MouseEvent} event - The mouse event.
     */
    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setMenuOpen(false);
        }
    };

    useEffect(() => {
        /**
         * @hook useEffect
         * @description Fetches user information once auth initialization is complete.
         * We wait for isLoading=false to ensure the token (local JWT or MSAL) is
         * guaranteed to be available before calling getAuthToken().
         */
        if (!isLoading && isAuthenticated) {
            userInfoRetryRef.current = 0;
            fetchUserInfo();
        } else if (!isLoading && !isAuthenticated) {
            setLoadingUserInfo(false);
        }
    }, [isAuthenticated, isLoading]);

    useEffect(() => {
        /**
         * @hook useEffect
         * @description Adds or removes a 'mousedown' event listener to the document
         * to handle closing the menu when clicking outside of it.
         */
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    return (
        <div className={styles.layout}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer} ref={menuRef}>
                    <Link to="/" className={styles.headerTitleContainer}>
                        <img src="https://www.vinci-energies.ma/app/uploads/sites/81/2021/09/Actemium-1.png" alt="Actemium" className={styles.headerLogo} />
                    </Link>
                    <nav role="navigation">
                        <ul className={`${styles.headerNavList} ${menuOpen ? styles.show : ""}`}>
                            {isAuthenticated && (
                                <>
                                    <li>
                                        <NavLink
                                            to="/chat"
                                            className={({ isActive }: { isActive: boolean }) =>
                                                `${styles.headerNavPageLink} ${isActive ? styles.headerNavPageLinkActive : ""}`
                                            }
                                        >
                                            <Icon iconName="Chat" className={styles.navIcon} />
                                            {"Chat"}
                                        </NavLink>
                                    </li>
                                    {!loadingUserInfo && userInfo?.can_manage_documents && (
                                        <li>
                                            <NavLink
                                                to="/admin"
                                                className={({ isActive }: { isActive: boolean }) =>
                                                    `${styles.headerNavPageLink} ${styles.headerNavPageLinkAdmin} ${isActive ? styles.headerNavPageLinkActive : ""}`
                                                }
                                            >
                                                <Icon iconName="Settings" className={styles.navIcon} />
                                                {"Gestion des documents"}
                                            </NavLink>
                                        </li>
                                    )}
                                    {!loadingUserInfo && ((userInfo?.categories_google?.length ?? 0) > 0 || userInfo?.isDev) && (
                                        <li>
                                            <NavLink
                                                to="/ata"
                                                className={({ isActive }: { isActive: boolean }) =>
                                                    `${styles.headerNavPageLink} ${isActive ? styles.headerNavPageLinkActive : ""}`
                                                }
                                            >
                                                <Icon iconName="DocumentSearch" className={styles.navIcon} />
                                                {"Documentation Technique"}
                                            </NavLink>
                                        </li>
                                    )}
                                </>
                            )}
                        </ul>
                    </nav>
                    <div className={styles.loginMenuContainer}>
                        {isAuthenticated && (
                            <div className={styles.authActions}>
                                {isLocalAuth ? (
                                    <Link to="/profile" className={styles.profileLink}>
                                        {username}
                                    </Link>
                                ) : (
                                    // This will now correctly apply to both MSAL and Google logins
                                    <span className={styles.username}>{username}</span>
                                )}
                                <button className={styles.logoutButton} onClick={logout}>
                                    {t("logout")}
                                </button>
                            </div>
                        )}
                        <IconButton
                            iconProps={{ iconName: "GlobalNavButton" }}
                            className={styles.menuToggle}
                            onClick={toggleMenu}
                            ariaLabel={t("labels.toggleMenu")}
                        />
                    </div>
                </div>
            </header>

            {/* Renders the child route's component */}
            <Outlet />
        </div>
    );
};

export default Layout;
